/*
    Copyright (c) 2022 Alethea Katherine Flowers.
    Published under the standard MIT License.
    Full text available at: https://opensource.org/licenses/MIT
*/

import { Barrier, later } from "../../base/async";
import { Disposables, type IDisposable } from "../../base/disposable";
import type { PanAndZoomOptions } from "../../base/dom/pan-and-zoom";
import { listen } from "../../base/events";
import { no_self_recursion } from "../../base/functions";
import { BBox, Vec2 } from "../../base/math";
import { Color, Renderer } from "../../graphics";
import {
  KiCanvasLoadEvent,
  KiCanvasMouseMoveEvent,
  type KiCanvasEventMap,
} from "./events";
import { ViewLayerSet } from "./view-layers";
import { Viewport } from "./viewport";

let setupMutex: Promise<void> | null = null

export abstract class Viewer extends EventTarget {
  public renderer: Renderer;
  public viewport: Viewport;
  public layers: ViewLayerSet;
  public mouse_position: Vec2 = new Vec2(0, 0);
  public loaded = new Barrier();

  protected disposables = new Disposables();
  protected setup_finished = new Barrier();

  #selected: BBox | null;

  constructor(
    public canvas: HTMLCanvasElement,
    protected interactive = true,
  ) {
    super();
  }

  dispose() {
    this.disposables.dispose();
  }

  override addEventListener<K extends keyof KiCanvasEventMap>(
    type: K,
    listener:
      | ((this: Viewer, ev: KiCanvasEventMap[K]) => void)
      | { handleEvent: (ev: KiCanvasEventMap[K]) => void }
      | null,
    options?: boolean | AddEventListenerOptions,
  ): IDisposable;
  override addEventListener(
    type: string,
    listener: EventListener | null,
    options?: boolean | AddEventListenerOptions,
  ): IDisposable {
    super.addEventListener(type, listener, options);
    return {
      dispose: () => {
        this.removeEventListener(type, listener, options);
      },
    };
  }

  protected abstract create_renderer(canvas: HTMLCanvasElement): Renderer;

  async setOptions(options: PanAndZoomOptions) {
    this.viewport.setOptions(options);
  }

  async setup(options: PanAndZoomOptions) {
    if (setupMutex === null) {
      setupMutex = new Promise((resolve) => {
        this.renderer = this.disposables.add(this.create_renderer(this.canvas));

        this.renderer.setup().then(() => {

          this.viewport = this.disposables.add(
            new Viewport(this.renderer, () => {
              this.on_viewport_change();
            }),
          );

          if (this.interactive) {
            this.viewport.enable_pan_and_zoom(0.5, 190);

            this.disposables.add(
              listen(this.canvas, "mousemove", (e) => {
                this.on_mouse_change(e);
              }),
            );

            this.disposables.add(
              listen(this.canvas, "panzoom", (e) => {
                this.on_mouse_change(e as MouseEvent);
              }),
            );

            this.disposables.add(
              listen(this.canvas, "click", (e) => {
                const items = this.layers.query_point(this.mouse_position);
                this.on_pick(this.mouse_position, items);
              }),
            );
          }

          this.setup_finished.open();
          resolve();
        });
      });

      await setupMutex;
      setupMutex = null;
    } else {
      setTimeout(() => {
        this.setup(options);
      }, 0);
    }

  }

  protected on_viewport_change() {
    if (this.interactive) {
      this.draw();
    }
  }

  protected on_mouse_change(e: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const new_position = this.viewport.camera.screen_to_world(
      new Vec2(e.clientX - rect.left, e.clientY - rect.top),
    );

    if (
      this.mouse_position.x != new_position.x ||
      this.mouse_position.y != new_position.y
    ) {
      this.mouse_position.set(new_position);
      this.dispatchEvent(new KiCanvasMouseMoveEvent(this.mouse_position));
    }
  }

  public abstract load(src: any): Promise<void>;

  protected resolve_loaded(value: boolean) {
    if (value) {
      this.loaded.open();
      this.dispatchEvent(new KiCanvasLoadEvent());
    }
  }

  public abstract paint(): void;

  protected on_draw() {
    this.renderer.clear_canvas();

    if (!this.layers) {
      return;
    }

    // Render all layers in display order (back to front)
    let depth = 0.01;
    const camera = this.viewport.camera.matrix;
    const should_dim = this.layers.is_any_layer_highlighted();

    for (const layer of this.layers.in_display_order()) {
      if (layer.visible && layer.graphics) {
        let alpha = layer.opacity;

        if (should_dim && !layer.highlighted) {
          alpha = 0.25;
        }

        layer.graphics.render(camera, depth, alpha);
        depth += 0.01;
      }
    }
  }

  public draw() {
    if (!this.viewport) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.on_draw();
    });
  }

  protected on_pick(
    mouse: Vec2,
    items: ReturnType<ViewLayerSet["query_point"]>,
  ) {
    let selected = null;

    for (const { bbox } of items) {
      selected = bbox;
      break;
    }

    this.select(selected);
  }

  public select(item: BBox | null) {
    // Disabling for now.
    // this.selected = item;
  }

  public get selected(): BBox | null {
    return this.#selected;
  }

  public set selected(bb: BBox | null) {
    this._set_selected(bb);
  }

  @no_self_recursion
  private _set_selected(bb: BBox | null) {
    // const previous = this.#selected;
    this.#selected = bb?.copy() || null;

    // Notify event listeners
    // this.dispatchEvent(
    //   new KiCanvasSelectEvent({
    //     item: this.#selected?.context,
    //     previous: previous?.context,
    //   }),
    // );

    later(() => this.paint_selected());
  }

  public get selection_color() {
    return Color.white;
  }

  protected paint_selected() {
    // const layer = this.layers.overlay;

    // layer.clear();

    // if (this.#selected) {
    //   const bb = this.#selected.copy().grow(this.#selected.w * 0.1);
    //   this.renderer.start_layer(layer.name);

    //   this.renderer.line(
    //     Polyline.from_BBox(bb, 0.254, this.selection_color),
    //   );

    //   this.renderer.polygon(Polygon.from_BBox(bb, this.selection_color));

    //   layer.graphics = this.renderer.end_layer();

    //   layer.graphics.composite_operation = "overlay";
    // }

    this.draw();
  }

  abstract zoom_to_page(): void;

  zoom_to_selection() {
    if (!this.selected) {
      return;
    }
    this.viewport.camera.bbox = this.selected.grow(10);
    this.draw();
  }
}
